<template>
  <div class="atha-page">

    <!-- Toolbar -->
    <div class="atha-toolbar">
      <button type="button" class="btn btn-light btn-sm" @click="$router.back()">
        <i class="bi bi-arrow-left mr-1"></i> Back
      </button>
      <div class="ml-auto d-flex align-items-center" style="gap:.5rem">
        <span v-if="event" class="text-muted small mr-2">
          <i class="bi bi-file-earmark-ruled mr-1"></i>
          {{ event.title }}
        </span>
        <label v-if="event" for="atha-prepared-by" class="text-muted small mb-0">Prepared by:</label>
        <select
          v-if="event"
          id="atha-prepared-by"
          v-model="preparedBySelection"
          class="form-control form-control-sm atha-signatory-select"
        >
          <option v-for="opt in preparedByOptions" :key="opt.name" :value="opt.name">{{ opt.name }}</option>
        </select>
        <button type="button" class="btn btn-primary btn-sm" :disabled="!event || loading" @click="openPrintWindow">
          <i class="bi bi-printer-fill mr-1"></i> Print / Save PDF
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="atha-state text-muted">
      <span class="spinner-border spinner-border-sm mr-2"></span> Loading RBA-ATHA data…
    </div>
    <div v-else-if="!event" class="atha-state text-muted">
      <i class="bi bi-exclamation-circle mr-1"></i> Activity not found.
    </div>

    <!-- Preview -->
    <div v-else class="atha-preview-wrap">
      <div v-if="missingRbaFields.length" class="atha-preview-hint atha-preview-hint-warn">
        <i class="bi bi-exclamation-triangle mr-1"></i>
        Some RBA sections are empty. Edit the activity and fill in the <strong>RBA-ATHA Details</strong> panel:
        {{ missingRbaFields.join(", ") }}.
      </div>
      <div class="atha-preview-hint">
        <i class="bi bi-info-circle mr-1"></i>
        This is a preview. Click <strong>Print / Save PDF</strong> to open the printable document.
      </div>
      <div class="atha-preview-card">

        <table class="prev-ft">
          <colgroup>
            <col style="width:27%">
            <col style="width:18%">
            <col style="width:10%">
            <col style="width:30%">
            <col style="width:15%">
          </colgroup>

          <!-- Letterhead + Title (single row) -->
          <tr>
            <td colspan="5" class="prev-header-cell">
              <div class="prev-header-top">
                <div class="prev-logo-area mt-3">
                  <img src="/ched-logo.png" alt="CHED" class="prev-logo-img" @error="onLogoError" />
                  <div v-if="logoFailed" class="prev-logo-placeholder"><i class="bi bi-shield-fill"></i></div>
                </div>
                <div class="prev-header-agency">
                  <div class="prev-agency">COMMISSION ON HIGHER EDUCATION</div>
                  <div class="prev-address">HEDC Bldg., C.P. Garcia Ave., UP Diliman, Quezon City</div>
                </div>
              </div>
              <div class="prev-header-title mb-3">
                <div class="prev-doc-title">REQUEST FOR BUDGET APPROVAL AND AUTHORITY TO HOLD ACTIVITY (RBA-ATHA)</div>
                <div class="prev-revised">(revised 2025)</div>
              </div>
            </td>
          </tr>

          <!-- Office / Date / No -->
          <tr>
            <td colspan="3" class="prev-meta-cell">
              <div class="prev-lc">OFFICE/DIVISION/SECTION/UNIT:</div>
              <div class="prev-meta-value prev-meta-value-center bold">CHED CARAGA REGIONAL OFFICE</div>
            </td>
            <td class="prev-meta-cell">
              <div class="prev-lc">DATE SUBMITTED:</div>
              <div class="prev-meta-value prev-meta-value-center">{{ formatDate(event.atha_date_submitted) || '—' }}</div>
            </td>
            <td class="prev-meta-cell prev-meta-narrow">
              <div class="prev-lc">NO.</div>
              <div class="prev-meta-value prev-meta-value-center">{{ event.atha_no || '' }}</div>
            </td>
          </tr>

          <!-- Program title + fund -->
          <tr>
            <td colspan="4" class="prev-meta-cell prev-program-cell">
              <div class="prev-lc">PROGRAM/ACTIVITY/PROJECT DESCRIPTION AND TITLE:</div>
              <div class="prev-program-title">{{ event.title }}</div>
              <div class="prev-program-narrative">{{ event.purpose || '—' }}</div>
            </td>
            <td class="prev-meta-cell prev-fund-cell">
              <div class="prev-lc prev-lc-mixed">Source of Fund:</div>
              <div class="prev-meta-value">{{ event.source_of_fund || '—' }}</div>
            </td>
          </tr>

          <tr>
            <td class="prev-lc" colspan="2">TO BE HELD ON:</td>
            <td class="prev-lc" colspan="3">TO BE HELD AT:</td>
          </tr>
          <tr>
            <td class="prev-vc" colspan="2">{{ formatDateRange(event.start_date_time, event.end_date_time) || '—' }}</td>
            <td class="prev-vc" colspan="3">{{ event.location || '—' }}</td>
          </tr>
          <tr>
            <td class="prev-lc" colspan="5">OBJECTIVE OF THE PROGRAM/ACTIVITY/PROJECT</td>
          </tr>
          <tr>
            <td class="prev-content-cell" colspan="5">{{ displayObjectives || '—' }}</td>
          </tr>
          <tr>
            <td class="prev-lc" colspan="5">OUTPUT OF THE PROGRAM/ACTIVITY/PROJECT</td>
          </tr>
          <tr>
            <td class="prev-content-cell" colspan="5">{{ displayOutput || '—' }}</td>
          </tr>
          <tr>
            <td class="prev-lc" colspan="5">
              TARGET PARTICIPANTS (Please enumerate and specify)
              <span class="prev-lc-note">(Use separate sheet if necessary)</span>
            </td>
          </tr>
          <tr>
            <td class="prev-content-cell" colspan="5">{{ displayParticipants || '—' }}</td>
          </tr>
        </table>

        <!-- Resource Persons preview -->
        <div class="prev-section">
          <div class="prev-section-label">RESOURCE PERSONS / CONSULTANTS / LECTURERS / FACILITATORS</div>
          <table class="prev-table mt-1">
            <thead>
              <tr><th>Name</th><th>Expertise</th><th>Company/Agency</th></tr>
            </thead>
            <tbody>
              <tr v-for="(rp, i) in (event.resource_persons || [])" :key="i">
                <td>{{ rp.name }}</td><td>{{ rp.expertise }}</td><td>{{ rp.company }}</td>
              </tr>
              <tr v-if="!event.resource_persons || !event.resource_persons.length">
                <td colspan="3" class="text-muted small">None specified</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Expenditures preview -->
        <div class="prev-section">
          <div class="prev-section-label">ESTIMATED EXPENDITURES</div>
          <table class="prev-table mt-1">
            <thead>
              <tr>
                <th>Item</th><th>Qty</th><th>Cost/Unit</th><th>Days</th>
                <th>Total</th><th>Fund Transfer</th><th>Direct Payment</th><th>Cash Advance</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(exp, i) in (event.estimated_expenditures || [])" :key="i">
                <td>{{ exp.item }}</td>
                <td class="num">{{ exp.quantity }}</td>
                <td class="num">{{ formatNum(exp.cost_per_unit) }}</td>
                <td class="num">{{ exp.days }}</td>
                <td class="num">{{ formatNum(exp.total) }}</td>
                <td class="num">{{ formatNum(exp.fund_transfer) }}</td>
                <td class="num">{{ formatNum(exp.direct_payment) }}</td>
                <td class="num">{{ formatNum(exp.cash_advance) }}</td>
              </tr>
              <tr v-if="!event.estimated_expenditures || !event.estimated_expenditures.length">
                <td colspan="8" class="text-muted small">No expenditure items</td>
              </tr>
              <tr v-else class="prev-total-row">
                <td colspan="4"><strong>TOTAL</strong></td>
                <td class="num"><strong>{{ formatNum(totalCost) }}</strong></td>
                <td class="num"><strong>{{ formatNum(totalFundTransfer) }}</strong></td>
                <td class="num"><strong>{{ formatNum(totalDirectPayment) }}</strong></td>
                <td class="num"><strong>{{ formatNum(totalCashAdvance) }}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PrintAthaPage",
  data() {
    return {
      loading: true,
      event: null,
      logoFailed: false,
      preparedBySelection: "CRISELDO C. CALINAWAN, PhD, PECE",
      preparedByOptions: [
        { name: "CRISELDO C. CALINAWAN, PhD, PECE", title: "Education Supervisor II" },
        { name: "AMY C. SAAVEDRA, MSCA", title: "Education Supervisor II" },
      ],
    };
  },
  computed: {
    preparedBy() {
      return (
        this.preparedByOptions.find((o) => o.name === this.preparedBySelection) ||
        this.preparedByOptions[0]
      );
    },
    totalCost() { return this.sum("total"); },
    totalFundTransfer() { return this.sum("fund_transfer"); },
    totalDirectPayment() { return this.sum("direct_payment"); },
    totalCashAdvance() { return this.sum("cash_advance"); },
    displayObjectives() {
      return this.formatListText(this.event?.program_objectives || "");
    },
    displayOutput() {
      return this.formatListText(this.event?.program_output || "");
    },
    displayParticipants() {
      return this.formatListText(this.event?.target_participants || "");
    },
    missingRbaFields() {
      if (!this.event) return [];
      const missing = [];
      if (!String(this.event.purpose || "").trim()) missing.push("Program Description");
      if (!String(this.event.source_of_fund || "").trim()) missing.push("Source of Fund");
      if (!String(this.event.program_objectives || "").trim()) missing.push("Objectives");
      if (!String(this.event.program_output || "").trim()) missing.push("Output");
      if (!String(this.event.target_participants || "").trim()) missing.push("Target Participants");
      if (!String(this.event.location || "").trim()) missing.push("Location");
      return missing;
    },
  },
  methods: {
    sum(key) {
      return (this.event?.estimated_expenditures || []).reduce((s, e) => s + (Number(e[key]) || 0), 0);
    },
    formatDate(val) {
      if (!val) return "";
      const d = new Date(val);
      if (isNaN(d)) return val;
      return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    },
    formatDateRange(start, end) {
      if (!start) return "";
      const s = this.formatDate(start);
      if (!end || end === start) return s;
      return `${s} – ${this.formatDate(end)}`;
    },
    formatNum(val) {
      if (val === null || val === undefined || val === "") return "";
      const n = Number(val);
      if (isNaN(n)) return "";
      return n.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    escHtml(str) {
      return String(str || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    formatListText(text) {
      const lines = String(text || "")
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
      if (!lines.length) return "";
      return lines
        .map((line, index) => (/^\d+[\.\)]\s/.test(line) ? line : `${index + 1}. ${line}`))
        .join("\n");
    },
    onLogoError() {
      this.logoFailed = true;
    },
    load() {
      const id = this.$route.params.id;
      axios.get(`api/events/${id}/atha`)
        .then((res) => { this.event = res.data; })
        .catch(() => { toastr.error("Failed to load RBA-ATHA data."); })
        .finally(() => { this.loading = false; });
    },

    /* ── Print window ───────────────────────────────────────── */
    openPrintWindow() {
      const e = this.event;
      if (!e) return;

      const preparedBy = this.preparedBy;
      const expenditures = e.estimated_expenditures || [];
      const resourcePersons = e.resource_persons || [];

      const totalCost = this.totalCost;
      const totalFund = this.totalFundTransfer;
      const totalDirect = this.totalDirectPayment;
      const totalCash = this.totalCashAdvance;

      const MIN_EXP_ROWS = 6;
      const MIN_RP_ROWS = 4;

      const rpRows = [...resourcePersons];
      while (rpRows.length < MIN_RP_ROWS) rpRows.push({ name: "", expertise: "", company: "" });

      const expRows = [...expenditures];
      while (expRows.length < MIN_EXP_ROWS) expRows.push(null);

      const f = (v) => this.formatNum(v);
      const fd = (v) => this.formatDate(v);
      const fdr = (s, en) => this.formatDateRange(s, en);
      const esc = (v) => this.escHtml(v);
      const fl = (v) => this.escHtml(this.formatListText(v));
      const logoUrl = `${window.location.origin}/ched-logo.png`;

      const rpHtml = rpRows.map((rp) => `
        <tr class="rp-row">
          <td style="width:33%">${esc(rp ? (rp.name || "") : "")}</td>
          <td style="width:34%">${esc(rp ? (rp.expertise || "") : "")}</td>
          <td style="width:33%">${esc(rp ? (rp.company || "") : "")}</td>
        </tr>`).join("");

      const expHtml = expRows.map((exp, idx) => exp ? `
        <tr class="exp-data">
          <td>${idx + 1}. ${esc(exp.item || "")}</td>
          <td class="tc">${exp.quantity != null ? exp.quantity : ""}</td>
          <td class="tr">${f(exp.cost_per_unit)}</td>
          <td class="tc">${exp.days != null ? exp.days : ""}</td>
          <td class="tr">${f(exp.total)}</td>
          <td class="tr">${f(exp.fund_transfer)}</td>
          <td class="tr">${f(exp.direct_payment)}</td>
          <td class="tr">${f(exp.cash_advance)}</td>
        </tr>` : `<tr class="exp-data"><td>&nbsp;</td><td></td><td></td><td></td><td></td><td></td><td></td><td></td></tr>`).join("");

      const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>RBA-ATHA – ${esc(e.title || "Activity")}</title>
<style>
* { margin:0; padding:0; box-sizing:border-box; }

body {
  font-family: Arial, Helvetica, sans-serif;
  font-size: 9pt;
  color: #000;
  background: #fff;
}

/* ── Page wrapper ─────────────────────────────────── */
.page {
  width: 210mm;
  min-height: 297mm;
  padding: 12mm 14mm 12mm;
  margin: 0 auto;
}

/* ── Header ──────────────────────────────────────── */
.header-cell {
  padding: 10px 10px 10px;
}
.header-top {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
}
.doc-logo {
  width: 66px;
  height: 66px;
  object-fit: contain;
  flex-shrink: 0;
  display: block;
}
.header-agency {
  text-align: left;
}
.doc-agency {
  font-size: 14pt;
  font-weight: bold;
  letter-spacing: 0.3px;
  line-height: 1.2;
}
.doc-address {
  font-size: 8pt;
  line-height: 1.3;
  margin-top: 1px;
}
.header-title {
  text-align: center;
  margin-top: 5px;
  padding-top: 4px;
}
.doc-form-title {
  font-size: 11.5pt;
  font-weight: bold;
  line-height: 1.2;
  text-transform: uppercase;
  white-space: nowrap;
}
.doc-revised {
  font-size: 8pt;
  margin-top: 1px;
}
.meta-cell {
  padding: 4px 6px 8px;
  min-height: 42px;
  vertical-align: top;
}
.meta-cell .lc {
  padding: 0;
}
.meta-val {
  font-size: 9pt;
  line-height: 1.35;
  margin-top: 4px;
  min-height: 18px;
}
.meta-val-center {
  text-align: center;
}
.meta-val-bold {
  font-weight: bold;
}

/* ── Main form table ─────────────────────────────── */
.ft {
  width: 100%;
  border-collapse: collapse;
  border: 1.5px solid #000;
  table-layout: fixed;
}
.ft td, .ft th {
  border: 1px solid #000;
  vertical-align: top;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* Label cells (field captions) */
.lc {
  font-size: 7.5pt;
  font-weight: bold;
  padding: 2px 5px;
  vertical-align: top;
}
/* Value cells */
.vc {
  font-size: 9pt;
  padding: 3px 5px;
  vertical-align: top;
}
.vc-bold { font-weight: bold; }

/* Tall multi-line cells */
.program-block {
  padding: 4px 6px 8px;
  vertical-align: top;
}
.program-title {
  font-weight: bold;
  font-size: 9pt;
  margin-top: 4px;
  margin-bottom: 6px;
}
.program-narrative {
  font-size: 8.5pt;
  line-height: 1.55;
  white-space: pre-wrap;
  min-height: 120px;
}
.fund-cell {
  vertical-align: top;
  width: 15%;
}
.content-cell {
  font-size: 9pt;
  line-height: 1.6;
  padding: 5px 6px;
  min-height: 75px;
  vertical-align: top;
  white-space: pre-wrap;
}
.participants-cell {
  font-size: 9pt;
  line-height: 1.6;
  padding: 5px 6px;
  min-height: 65px;
  vertical-align: top;
  white-space: pre-wrap;
}
.held-val {
  font-size: 9pt;
  padding: 3px 6px;
  min-height: 22px;
  vertical-align: top;
}

/* ── Resource persons ────────────────────────────── */
.rp-col-hdr {
  text-align: center;
  font-weight: bold;
  font-size: 8pt;
  padding: 3px 5px;
  border: 1px solid #000;
  background: #f8f8f8;
}
.rp-row td {
  border-left: 1px solid #000;
  border-right: 1px solid #000;
  border-bottom: 1px solid #bbb;
  border-top: none;
  padding: 3px 5px;
  height: 21px;
  font-size: 8.5pt;
  vertical-align: bottom;
}

/* ── Expenditure table ───────────────────────────── */
.exp-table thead th {
  font-size: 7pt;
  text-align: center;
  font-weight: bold;
  line-height: 1.3;
  vertical-align: middle;
}
.exp-table .exp-item-hdr {
  text-align: left;
  font-weight: bold;
  font-size: 8pt;
  vertical-align: middle;
}
.exp-table .exp-data td {
  height: 18px;
  font-size: 8.5pt;
  vertical-align: middle;
}
.exp-table .exp-total td {
  font-weight: bold;
  font-size: 8.5pt;
  background: #f5f5f5;
}
.tc { text-align: center; }
.tr { text-align: right; }

/* ── Signature blocks ────────────────────────────── */
.sig-lbl-row td {
  font-size: 8pt;
  padding: 3px 6px;
  vertical-align: top;
}
.sig-name-row td {
  vertical-align: bottom;
  text-align: center;
  padding: 4px 6px;
  height: 60px;
}
.sn {
  font-weight: bold;
  font-size: 8.5pt;
  text-decoration: underline;
  margin-bottom: 1px;
}
.sp { font-size: 7.5pt; }

/* ── Print rules ─────────────────────────────────── */
@media screen {
  body { background: #e0e0e0; }
  .page {
    background: #fff;
    box-shadow: 0 2px 16px rgba(0,0,0,.22);
    margin: 10mm auto 6mm;
    border-radius: 2px;
  }
}
@media print {
  @page { size: A4 portrait; margin: 0; }
  body { background: #fff; }
  .page { width: 100%; min-height: auto; padding: 12mm 14mm; box-shadow: none; margin: 0; }
  .page2 { page-break-before: always; break-before: page; }
  .ft, .ft td, .ft th { page-break-inside: avoid; }
}
</style>
</head>
<body>
<div class="page">

<!-- ════════════════════════════════════════════════
     PAGE 1 — Main form (header through participants)
════════════════════════════════════════════════ -->
<table class="ft">
  <colgroup>
    <col style="width:27%">
    <col style="width:18%">
    <col style="width:10%">
    <col style="width:30%">
    <col style="width:15%">
  </colgroup>

  <tr>
    <td colspan="5" class="header-cell">
      <div class="header-top">
        <img class="doc-logo" src="${logoUrl}" alt="CHED Logo" onerror="this.style.visibility='hidden'">
        <div class="header-agency">
          <div class="doc-agency">COMMISSION ON HIGHER EDUCATION</div>
          <div class="doc-address">HEDC Bldg., C.P. Garcia Ave., UP Diliman, Quezon City</div>
        </div>
      </div>
      <div class="header-title">
        <div class="doc-form-title">REQUEST FOR BUDGET APPROVAL AND AUTHORITY TO HOLD ACTIVITY (RBA-ATHA)</div>
        <div class="doc-revised">(revised 2025)</div>
      </div>
    </td>
  </tr>

  <!-- ── Office / Date / No ─────────────────────── -->
  <tr>
    <td class="meta-cell" colspan="3">
      <div class="lc"><strong>OFFICE/DIVISION/SECTION/UNIT:</strong></div>
      <div class="meta-val meta-val-center meta-val-bold">CHED CARAGA REGIONAL OFFICE</div>
    </td>
    <td class="meta-cell">
      <div class="lc"><strong>DATE SUBMITTED:</strong></div>
      <div class="meta-val meta-val-center">${fd(e.atha_date_submitted) || ""}</div>
    </td>
    <td class="meta-cell">
      <div class="lc"><strong>NO.</strong></div>
      <div class="meta-val meta-val-center">${esc(e.atha_no || "")}</div>
    </td>
  </tr>

  <!-- ── Program title / narrative / fund ───────── -->
  <tr>
    <td class="meta-cell program-block" colspan="4">
      <div class="lc"><strong>PROGRAM/ACTIVITY/PROJECT DESCRIPTION AND TITLE:</strong></div>
      <div class="program-title">${esc(e.title || "")}</div>
      <div class="program-narrative">${esc(e.purpose || "")}</div>
    </td>
    <td class="meta-cell fund-cell">
      <div class="lc"><strong>Source of Fund:</strong></div>
      <div class="meta-val">${esc(e.source_of_fund || "")}</div>
    </td>
  </tr>

  <!-- ── To Be Held On / At ─────────────────────── -->
  <tr>
    <td class="lc" colspan="2"><strong>TO BE HELD ON:</strong></td>
    <td class="lc" colspan="3"><strong>TO BE HELD AT:</strong></td>
  </tr>
  <tr>
    <td class="held-val" colspan="2">${esc(fdr(e.start_date_time, e.end_date_time))}</td>
    <td class="held-val" colspan="3">${esc(e.location || "")}</td>
  </tr>

  <!-- ── Objective ──────────────────────────────── -->
  <tr>
    <td class="lc" colspan="5"><strong>OBJECTIVE OF THE PROGRAM/ACTIVITY/PROJECT</strong></td>
  </tr>
  <tr>
    <td class="content-cell" colspan="5">${fl(e.program_objectives || "")}</td>
  </tr>

  <!-- ── Output ─────────────────────────────────── -->
  <tr>
    <td class="lc" colspan="5"><strong>OUTPUT OF THE PROGRAM/ACTIVITY/PROJECT</strong></td>
  </tr>
  <tr>
    <td class="content-cell" colspan="5">${fl(e.program_output || "")}</td>
  </tr>

  <!-- ── Target Participants ────────────────────── -->
  <tr>
    <td class="lc" colspan="5">
      <strong>TARGET PARTICIPANTS (Please enumerate and specify)</strong>
      <span style="font-weight:normal;font-size:7pt;margin-left:6px">(Use separate sheet if necessary)</span>
    </td>
  </tr>
  <tr>
    <td class="participants-cell" colspan="5">${fl(e.target_participants || "")}</td>
  </tr>
</table>
</div><!-- end .page (page 1) -->

<!-- ════════════════════════════════════════════════
     PAGE 2 — Resource persons, expenditures, signatures
════════════════════════════════════════════════ -->
<div class="page page2">

<!-- ── Resource Persons ───────────────────────── -->
<table class="ft" style="margin-bottom:0">
  <colgroup>
    <col style="width:33%">
    <col style="width:34%">
    <col style="width:33%">
  </colgroup>
  <tr>
    <td class="lc" colspan="3">
      <strong>RESOURCE PERSONS/CONSULTANTS/LECTURER/FACILITATORS:</strong>
      <span style="font-weight:normal;font-size:7pt;margin-left:6px">(Use separate sheet if necessary)</span>
    </td>
  </tr>
  <tr>
    <td class="rp-col-hdr">NAME</td>
    <td class="rp-col-hdr">EXPERTISE</td>
    <td class="rp-col-hdr">COMPANY/AGENCY</td>
  </tr>
  ${rpHtml}
</table>

<!-- ── Estimated Expenditures ─────────────────── -->
<table class="ft exp-table" style="margin-top:-1px;margin-bottom:0;position:relative">
  <colgroup>
    <col style="width:22%">
    <col style="width:9%">
    <col style="width:12%">
    <col style="width:8%">
    <col style="width:12%">
    <col style="width:12%">
    <col style="width:12%">
    <col style="width:13%">
  </colgroup>
  <thead>
    <tr>
      <th class="exp-item-hdr" rowspan="2">ESTIMATED EXPENDITURES</th>
      <th rowspan="2">No. of<br>Participants/<br>Quantity</th>
      <th rowspan="2">Estimated<br>Expenses per<br>participant/<br>Cost per Unit</th>
      <th rowspan="2">Number<br>of days</th>
      <th rowspan="2">Total<br>Estimated<br>Cost</th>
      <th colspan="3">Mode of Disbursement</th>
    </tr>
    <tr>
      <th>FUND<br>TRANSFER</th>
      <th>DIRECT<br>PAYMENT</th>
      <th>CASH<br>ADVANCE</th>
    </tr>
  </thead>
  <tbody>
    ${expHtml}
    <tr class="exp-total">
      <td colspan="4" style="text-align:right;padding:3px 8px"><strong>TOTAL</strong></td>
      <td class="tr">${f(totalCost)}</td>
      <td class="tr">${f(totalFund)}</td>
      <td class="tr">${f(totalDirect)}</td>
      <td class="tr">${f(totalCash)}</td>
    </tr>
  </tbody>
</table>

<!-- ── Signatures ─────────────────────────────── -->
<table class="ft" style="margin-top:-1px;position:relative">
  <colgroup>
    <col style="width:25%">
    <col style="width:22%">
    <col style="width:28%">
    <col style="width:25%">
  </colgroup>
  <tr class="sig-lbl-row">
    <td colspan="2">Requested by:</td>
    <td>Allotment Available:</td>
    <td>Cash Available:</td>
  </tr>
  <tr class="sig-name-row">
    <td colspan="2">
      <div class="sn">${esc(preparedBy.name)}</div>
      <div class="sp">${esc(preparedBy.title)}</div>
    </td>
    <td>
      <div class="sn">NOEME ROSE D. MANI, MM, CPA</div>
      <div class="sp">SAO / OIC-Chief Administrative Officer</div>
    </td>
    <td>
      <div class="sn">GESELLE M. FRANCISCO, CPA</div>
      <div class="sp">Accountant III</div>
    </td>
  </tr>
  <tr class="sig-lbl-row">
    <td colspan="2">Recommending Approval:</td>
    <td colspan="2">Approved by:</td>
  </tr>
  <tr class="sig-name-row">
    <td colspan="2">
      <div class="sn">JULIA FELISA C. MARTINEZ, PhD</div>
      <div class="sp">Chief Education Program Specialist</div>
    </td>
    <td colspan="2">
      <div class="sn">NELIA A. ALIBIN, PhD</div>
      <div class="sp">Director IV</div>
    </td>
  </tr>
</table>

</div><!-- end .page (page 2) -->

<script>
  window.onload = function() { setTimeout(function(){ window.print(); }, 500); };
  window.onafterprint = function() { window.close(); };
<\/script>
</body>
</html>`;

      const win = window.open("", "_blank", "width=900,height=700");
      if (!win) {
        toastr.warning("Pop-up was blocked. Please allow pop-ups for this site and try again.");
        return;
      }
      win.document.write(html);
      win.document.close();
    },
  },
  mounted() { this.load(); },
};
</script>

<style scoped>
.atha-page {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 1rem;
}

.atha-toolbar {
  display: flex;
  align-items: center;
  max-width: 960px;
  margin: 0 auto 1rem;
}

.atha-signatory-select {
  width: auto;
  min-width: 240px;
}

.atha-state {
  text-align: center;
  padding: 4rem 1rem;
  font-size: 1rem;
}

.atha-preview-wrap {
  max-width: 960px;
  margin: 0 auto;
}

.atha-preview-hint-warn {
  background: rgba(224, 144, 31, 0.1);
  border-color: rgba(224, 144, 31, 0.25);
  color: #8a5a00;
  margin-bottom: 0.5rem;
}

.atha-preview-hint {
  padding: 0.6rem 1rem;
  border-radius: 8px;
  background: rgba(77, 141, 255, 0.08);
  border: 1px solid rgba(77, 141, 255, 0.15);
  color: #2b4299;
  font-size: 0.83rem;
  margin-bottom: 1rem;
}

.atha-preview-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  padding: 2rem;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 9.5pt;
  color: #000;
}

/* Preview — unified form table */
.prev-ft {
  width: 100%;
  border-collapse: collapse;
  border: 1.5px solid #000;
  table-layout: fixed;
  margin-top: 0;
}

.prev-ft td {
  border: 1px solid #000;
  vertical-align: top;
}

.prev-header-cell {
  padding: 10px 10px 10px;
}

.prev-header-top {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
}

.prev-logo-area {
  flex-shrink: 0;
}

.prev-logo-img {
  width: 66px;
  height: 66px;
  object-fit: contain;
  display: block;
}

.prev-logo-placeholder {
  width: 66px;
  height: 66px;
  border-radius: 50%;
  background: #003087;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
}

.prev-header-agency {
  text-align: left;
}

.prev-agency {
  font-size: 13pt;
  font-weight: bold;
  letter-spacing: 0.3px;
  line-height: 1.2;
}

.prev-address {
  font-size: 8pt;
  line-height: 1.3;
  margin-top: 1px;
}

.prev-header-title {
  text-align: center;
  margin-top: 5px;
  padding-top: 4px;
}

.prev-doc-title {
  font-size: 11.5pt;
  font-weight: bold;
  line-height: 1.2;
  text-transform: uppercase;
  white-space: nowrap;
}

.prev-revised {
  font-size: 8pt;
  margin-top: 1px;
}

.prev-meta-cell {
  padding: 4px 6px 8px;
  min-height: 42px;
}

.prev-meta-narrow {
  width: 15%;
}

.prev-meta-value {
  font-size: 9pt;
  line-height: 1.35;
  margin-top: 4px;
  min-height: 18px;
}

.prev-meta-value-center {
  text-align: center;
}

.prev-meta-value.bold,
.prev-program-title {
  font-weight: bold;
}

.prev-lc {
  font-size: 7.5pt;
  font-weight: bold;
  padding: 2px 5px;
  text-transform: uppercase;
  line-height: 1.2;
}

.prev-meta-cell .prev-lc {
  padding: 0;
}

.prev-lc-mixed {
  text-transform: none;
}

.prev-lc-note {
  font-weight: normal;
  font-size: 7pt;
  margin-left: 4px;
  text-transform: none;
}

.prev-vc {
  font-size: 9pt;
  padding: 3px 6px;
}

.prev-program-cell {
  vertical-align: top;
}

.prev-program-title {
  font-size: 9.5pt;
  margin-top: 4px;
  margin-bottom: 6px;
}

.prev-program-narrative {
  font-size: 8.5pt;
  line-height: 1.55;
  white-space: pre-wrap;
  min-height: 100px;
}

.prev-fund-cell {
  width: 15%;
  vertical-align: top;
}

.prev-content-cell,
.prev-participants-cell {
  font-size: 9pt;
  line-height: 1.6;
  white-space: pre-wrap;
  padding: 5px 6px;
  min-height: 55px;
}

/* Preview sections */
.prev-section {
  border: 1px solid #000;
  border-top: none;
  padding: 5px 6px;
}

.prev-section-label {
  font-size: 8pt;
  font-weight: bold;
  margin-bottom: 3px;
}

.prev-section-body {
  font-size: 9pt;
  line-height: 1.55;
  white-space: pre-wrap;
  min-height: 40px;
}

.prev-narrative { min-height: 80px; }
.prev-narrative-text { font-size: 8.5pt; line-height: 1.55; white-space: pre-wrap; }

/* Preview tables */
.prev-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 8.5pt;
  margin-top: 4px;
}

.prev-table th, .prev-table td {
  border: 1px solid #999;
  padding: 3px 5px;
  vertical-align: top;
}

.prev-table thead th {
  background: #f0f0f0;
  font-size: 7.5pt;
  text-align: center;
}

.prev-table td.num { text-align: right; }
.prev-total-row td { background: #f5f5f5; }
</style>
